import { LazyExoticComponent, JSX, lazy, useEffect, useLayoutEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAfterUpdate } from '@betgames/bg-tools';
import { LazyRoute } from '@bg-components/LazyRoute';
import { useStore } from '@betgames/bg-state-manager';
import { InitialPageSkeleton } from '@bg-components/Skeleton';

import {
    iframeIntegration,
    partnerSettingsStore,
    authService,
    useInterfaceClassName,
    useGameIdClassName,
    gamesMessagesEntity,
    messageQueueStore,
    useGameId,
    playerSettingsSelectors,
    playerSettingsStore,
    iosSoundHandlerStore,
    GameId,
    routeUrlGenerator,
    ClassicGameDefinition,
    videoStreamStore,
    useGameWebAPIIntegration,
    balanceService,
    chatService,
    useBeacon,
    usePlayerActivity,
} from '@bg-shared';

export const games: Partial<Record<GameId, LazyExoticComponent<() => JSX.Element>>> = {
    [GameId.SIX_PLUS_POKER]: lazy(() => import('./SixPlusPoker/SixPlusPoker')),

    [GameId.POKER]: lazy(() => import('./Poker/Poker')),

    [GameId.STS_POKER]: lazy(() => import('./STSPoker/STSPoker')),

    [GameId.WAR]: lazy(() => import('./War/War')),

    [GameId.BACCARAT]: lazy(() => import('./Baccarat/Baccarat')),

    [GameId.DICE_DUEL]: lazy(() => import('./DiceDuel/DiceDuel')),

    [GameId.WHEEL]: lazy(() => import('./Wheel/Wheel')),

    [GameId.LUCKY5]: lazy(() => import('./Lucky5/Lucky5')),

    [GameId.LUCKY6]: lazy(() => import('./Lucky6/Lucky6')),

    [GameId.LUCKY7]: lazy(() => import('./Lucky7/Lucky7')),

    [GameId.BETWAY_LUCKY7]: lazy(() => import('./BetwayLucky7/BetwayLucky7')),

    [GameId.RNG_WHEEL]: lazy(() => import('./RNGWheel/RNGWheel')),

    [GameId.RNG_FOOTBALL_GRID]: lazy(() => import('./RNGFootballGrid/RNGFootballGrid')),

    [GameId.RNG_LUCKY7]: lazy(() => import('./RNGLucky7/RNGLucky7')),

    [GameId.SHOVA52]: lazy(() => import('./Shova52/Shova52')),

    [GameId.LUCKY_KICKS]: lazy(() => import('./LuckyKicks/LuckyKicks')),

    [GameId.LUCKY_LENNY]: lazy(() => import('./LuckyLenny/LuckyLenny')),

    // Remote games (moved back to iframe-remotes with original structure)

    [GameId.STARZLE]: lazy(() => import('@iframe-remotes/starzle/src/app/Starzle')),

    [GameId.GIZA]: lazy(() => import('@iframe-remotes/giza/src/app/Giza')),

    ...ClassicGameDefinition.ROULETTE_GAMES_IDS.reduce(
        (result, id) => ({
            ...result,
            [id]: lazy(() => import('@iframe-remotes/roulette/src/app')),
        }),
        {},
    ),

    ...ClassicGameDefinition.SKYWARD_GAMES_IDS.reduce(
        (result, id) => ({
            ...result,
            [id]: lazy(() => import('@iframe-remotes/skyward/src/app')),
        }),
        {},
    ),
};

const factoryGames = ClassicGameDefinition.THIRD_PARTY_GAMES_IDS.reduce(
    (acc, gameId) => {
        const handler = ClassicGameDefinition.isHZGame(gameId)
            ? lazy(() => import('./ExternalGameFactory/HZGameFactory'))
            : lazy(() => import('./ExternalGameFactory/PLSGameFactory'));

        acc[gameId] = handler;
        return acc;
    },
    {} as Partial<
        Record<GameId, LazyExoticComponent<({ gameId }: { gameId: GameId }) => JSX.Element>>
    >,
);

export const Game = (): JSX.Element => {
    const gameId = useGameId();
    const isGuiMode = useStore(playerSettingsStore, playerSettingsSelectors.getGuiMode);
    const { partnerSettings } = partnerSettingsStore;
    const GameComponent = games[gameId];
    const FactoryGameComponent = factoryGames[gameId];
    const enabledGame = partnerSettings.availableGamesIds.includes(gameId);

    useGameIdClassName(gameId);
    useInterfaceClassName(isGuiMode);
    useGameWebAPIIntegration(gameId);
    usePlayerActivity(gameId);
    useBeacon();

    useEffect(() => {
        if (partnerSettingsStore.isApiSendBalancePerGame) {
            balanceService.ping();
        }
    }, [gameId]);

    useAfterUpdate(() => {
        if (enabledGame && partnerSettings.reinitSessionOnGameNavigation) {
            authService.reinitializeGameSession(gameId);
        }

        chatService.leaveChannel();
    }, [gameId]);

    useLayoutEffect(() => {
        videoStreamStore.ready = false;
        gamesMessagesEntity.resetMessages();
        messageQueueStore.resetMessages(); // Won messages
        iosSoundHandlerStore.resetStudioSounds();
        iframeIntegration.gameNavigation(gameId);
        playerSettingsStore.forcedGuiMode = false; // Reset bad connection state on switching the game
    }, [gameId]);

    if (!FactoryGameComponent && (!enabledGame || !GameComponent)) {
        return (
            <Routes>
                <Route
                    path="*"
                    element={<Navigate replace to={routeUrlGenerator.getGameUrl()} />}
                />
            </Routes>
        );
    }

    return (
        <LazyRoute Fallback={<InitialPageSkeleton />}>
            {FactoryGameComponent ? <FactoryGameComponent gameId={gameId} /> : <GameComponent />}
        </LazyRoute>
    );
};
